/**
 * 智能 Keep-Alive 管理器
 * 根据页面内存占用、访问频率等指标动态管理缓存
 */

import { AliveController } from "react-activation";

export interface PageCacheInfo {
  name: string;
  cacheKey: string;
  lastAccessed: number;
  accessCount: number;
  memoryWeight: number; // 页面内存权重 1-10
  priority: number; // 缓存优先级 1-10（数字越高优先级越高，越不容易被清理）
  createdAt: number; // 创建时间，用于保护最新页面
}

export interface SmartKeepAliveConfig {
  maxCacheSize: number; // 最大缓存页面数
  maxMemoryWeight: number; // 最大内存权重总和
  cleanupThreshold: number; // 清理阈值比例 0-1
  protectedRecentCount: number; // 保护最新加入的页面数量
}

class SmartKeepAliveManager {
  private cacheMap = new Map<string, PageCacheInfo>();
  private aliveController?: AliveController;
  
  private config: SmartKeepAliveConfig = {
    maxCacheSize: 6,
    maxMemoryWeight: 30,
    cleanupThreshold: 0.8,
    protectedRecentCount: 2 // 保护最新的2个页面
  };

  // 页面内存权重配置（仅包含实际使用 keep-alive 的页面）
  private pageWeights: Record<string, number> = {
    'home': 8,        // 首页
    'homepage': 6,            // 主页页面
  };

  // 页面优先级配置（仅包含实际使用 keep-alive 的页面）
  // 数字越高优先级越高，越不容易被清理
  private pagePriorities: Record<string, number> = {
    'home': 10,       // 首页最高优先级
    'homepage': 7,            // 主页页面
    'default': 5
  };

  constructor(controller?: AliveController) {
    this.aliveController = controller;
  }

  setAliveController(controller: AliveController) {
    this.aliveController = controller;
  }

  updateConfig(config: Partial<SmartKeepAliveConfig>) {
    this.config = { ...this.config, ...config };
  }

  /**
   * 页面访问时调用
   */
  onPageAccess(name: string, cacheKey: string) {
    const now = Date.now();
    const existing = this.cacheMap.get(name);
    
    if (existing) {
      // 更新现有记录
      existing.lastAccessed = now;
      existing.accessCount += 1;
    } else {
      // 创建新记录
      const info: PageCacheInfo = {
        name,
        cacheKey,
        lastAccessed: now,
        accessCount: 1,
        memoryWeight: this.pageWeights[name] || 3,
        priority: this.pagePriorities[name] || this.pagePriorities.default,
        createdAt: now
      };
      this.cacheMap.set(name, info);
    }

    // 检查是否需要清理
    this.checkAndCleanup();
  }

  /**
   * 检查并执行清理
   */
  private checkAndCleanup() {
    const cacheArray = Array.from(this.cacheMap.values());
    const totalWeight = cacheArray.reduce((sum, item) => sum + item.memoryWeight, 0);
    
    // 检查是否超过限制
    const exceedsSize = cacheArray.length > this.config.maxCacheSize;
    const exceedsWeight = totalWeight > this.config.maxMemoryWeight;
    
    if (exceedsSize || exceedsWeight) {
      this.performCleanup();
    }
  }

  /**
   * 执行智能清理 - 基于优先级的简化策略
   */
  private performCleanup() {
    const cacheArray = Array.from(this.cacheMap.values());
    
    // 按访问时间排序，找出最新的页面
    const sortedByCreation = [...cacheArray].sort((a, b) => b.lastAccessed - a.lastAccessed);
    const recentlyCreated = new Set(
      sortedByCreation.slice(0, this.config.protectedRecentCount).map(page => page.name)
    );
    
    // 获取可清理的页面（排除最新访问的页面）
    const removablePages = cacheArray.filter(page => {
      return !recentlyCreated.has(page.name);
    });
    
    if (removablePages.length === 0) {
      console.log('🚫 所有页面都被保护，无法清理');
      return;
    }
    
    // 按优先级和访问时间排序（优先级低、访问时间久的优先清理）
    const sortedPages = removablePages.sort((a, b) => {
      // 优先级越低，越容易被清理
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
      // 优先级相同时，访问时间越久远，越容易被清理
      return a.lastAccessed - b.lastAccessed;
    });
    
    // 计算需要清理的页面数量
    const targetSize = Math.floor(this.config.maxCacheSize * this.config.cleanupThreshold);
    const targetRemoveCount = Math.max(cacheArray.length - targetSize, 1);
    
    // 清理优先级最低的页面
    const toRemove = sortedPages.slice(0, Math.min(targetRemoveCount, sortedPages.length));
    
    // 执行清理
    toRemove.forEach(page => {
      this.removePage(page.name);
    });

    console.log(`🧹 Keep-Alive 智能清理完成:`, {
      移除页面: toRemove.map(p => ({
        name: p.name,
        priority: p.priority,
        isRecent: recentlyCreated.has(p.name)
      })),
      剩余页面数: cacheArray.length - toRemove.length,
      保护的最新页面: Array.from(recentlyCreated)
    });
  }

  /**
   * 移除页面缓存
   */
  private removePage(name: string) {
    this.cacheMap.delete(name);
    
    // 调用 react-activation 的清理方法
    if (this.aliveController) {
      try {
        this.aliveController.drop(name);
      } catch (error) {
        console.warn(`清理缓存失败: ${name}`, error);
      }
    }
  }

  /**
   * 手动清理指定页面
   */
  public clearPage(name: string) {
    this.removePage(name);
  }

  /**
   * 清理所有缓存
   */
  public clearAll() {
    const toRemove = Array.from(this.cacheMap.values());
    
    toRemove.forEach(page => {
      this.removePage(page.name);
    });
  }

  /**
   * 获取缓存统计信息
   */
  public getStats() {
    const cacheArray = Array.from(this.cacheMap.values());
    const totalWeight = cacheArray.reduce((sum, item) => sum + item.memoryWeight, 0);
    
    // 按访问时间排序，标记最新页面
    const sortedByCreation = [...cacheArray].sort((a, b) => b.lastAccessed - a.lastAccessed);
    const recentPages = new Set(
      sortedByCreation.slice(0, this.config.protectedRecentCount).map(page => page.name)
    );
    
    return {
      totalPages: cacheArray.length,
      totalWeight,
      config: this.config,
      pages: cacheArray
        .sort((a, b) => b.priority - a.priority) // 按优先级排序显示
        .map(page => ({
          name: page.name,
          accessCount: page.accessCount,
          memoryWeight: page.memoryWeight,
          priority: page.priority,
          lastAccessed: new Date(page.lastAccessed).toLocaleString(),
          createdAt: new Date(page.createdAt).toLocaleString(),
          isRecent: recentPages.has(page.name)
        }))
    };
  }
}

// 全局实例
export const smartKeepAliveManager = new SmartKeepAliveManager();

// // 开发环境下的调试方法
// if (process.env.NODE_ENV === 'development') {
//   (window as any).keepAliveDebug = {
//     getStats: () => smartKeepAliveManager.getStats(),
//     clearAll: () => smartKeepAliveManager.clearAll(),
//     clearPage: (name: string) => smartKeepAliveManager.clearPage(name),
//     updateConfig: (config: any) => smartKeepAliveManager.updateConfig(config)
//   };
// }